import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical from 'lib/supercritical';
import { tzmoment } from 'lib/tzmoment';
import { Issue } from 'data/models/Issue';
import { IssueSeverity, DiscrepantScheduleDateField } from 'server/enums/Issue';
import { IssueType } from 'data/models/IssueType';
import { ScheduleDateEntity } from 'data/models/ScheduleDateEntity';
import { ScheduleDateType } from 'data/models/ScheduleDateType';
import { ScheduleDateEventType } from 'data/models/ScheduleDateEventType';
import { PCBadge } from 'portcall/pcbadge/view';

export const 
    IssueCountBadge = SuperCritical(({ issues, onClick } : { issues : () => Issue[], onClick? : () => void }) =>
        <PCBadge className={"issue-count-badge" + (issues().some(i => i.Severity() >= IssueSeverity.Conflict) ? ' has-conflicts' : '')} 
            visible={issues().length > 0}
            onClick={onClick}
        >
            {issues().length || null}
        </PCBadge>),
    SmallConflictCountBadge = SuperCritical(({ conflictCount } : { conflictCount : () => number }) =>
        conflictCount() ? <span className="small-conflict-count-badge">{conflictCount()}</span> : null
    ),
    SmallConflictCountBadgeWithTooltip = SuperCritical(({ conflicts } : { conflicts: () => Issue[] }) => {
        return (
            <div>
                {conflicts().length ? <div className="small-conflict-count-badge issue-badge-with-tooltip">
                    {conflicts().length}
                    <div className="tooltip-popup">
                        <div className="tooltip-caret"></div>
                        <div className="tooltip-text">
                            {conflicts().map(i => <IssueDescriptionForTooltip key={i.Id()} issue={i} />)}
                        </div>
                    </div>
                </div> : null}
            </div>
        );
    }),
    IssueCountPillBadge = SuperCritical(({ className, issues } : { className? : string, issues : () => Issue[] }) =>
        issues().length > 0 ?
            <span
                className={cx(
                    'issue-count-pill-badge',
                    { 'issue-count-pill-badge--has-conflicts': issues().some(i => i.Severity() >= IssueSeverity.Conflict) },
                    className
                )}
            >
                {issues().length}
            </span> :
            null
    ),
    IssueCountPillBadgeWithTooltip = SuperCritical(({ className, issues } : { className? : string, issues : () => Issue[] }) =>
        issues().length > 0 ?
            <span className={cx('issue-badge-with-tooltip', className)}>
                <IssueCountPillBadge issues={issues} />
                <div className="tooltip-popup">
                    <div className="tooltip-caret"></div>
                    <div className="tooltip-text">
                        {issues().map(i => <IssueDescriptionForTooltip key={i.Id()} issue={i} />)}
                    </div>
                </div>
            </span> :
            null
    ),
    IssueSummary = SuperCritical(({ issue: { Severity, Type }, className, onClick } : { issue : Issue, className? : string, onClick? : () => void }) => 
        <span className={"issue-summary " + (className || '') + (onClick ? ' has-click' : '') + (Severity() >= IssueSeverity.Conflict ? ' conflict' : '')} onClick={onClick}>
            {
                Type() === IssueType.ReservationOverlapsOther ?
                    "Overlaps existing reservation" :
                Type() === IssueType.ReservationOverlapsShift ?
                    "Overlaps a reservation in this portcall" :
                Type() === IssueType.ReservationOverlapsVesselSchedule ?
                    "Overlaps same vessel at another location" :
                Type() === IssueType.ReservationOverlapsBerthClosure ?
                    "Overlaps berth closure" :
                Type() === IssueType.ReservationExceedsBerthDepth ?
                    "Exceeds berth's max. depth" :
                Type() === IssueType.ReservationExceedsBerthLength ?
                    "Exceeds berth's max. length" :
                Type() === IssueType.ReservationLacksKnownVessel ?
                    "Requires a vessel at this location" :
                Type() === IssueType.ScheduleDisagreesWithEnteredDate ?
                    "Schedule differs from the forecasted ETA" :
                null
            }
        </span>
    ),
    // TH 11/18/2016 -- More discursive than some of the other displays here.
    // Picks the highest priority issue type. Suitable for insertion into "This reservation is in conflict because it..."
    ConflictReasonText = (issues : () => Issue[]) => {
        const top = Issue.TopIssue(issues().filter(i => i.Severity() >= IssueSeverity.Conflict));
        if (!top) return '';
        return (
            top.Type() === IssueType.ReservationExceedsBerthLength ?
                'exceeds the berth\'s max length' :
            top.Type() === IssueType.ReservationExceedsBerthDepth ?
                'exceeds the berth\'s max depth' :
            top.Type() === IssueType.ReservationLacksKnownVessel ?
                'requires a vessel at this location' :
            top.Type() === IssueType.ReservationOverlapsOther ?
                'overlaps an existing reservation' :
            top.Type() === IssueType.ReservationOverlapsShift ?
                'overlaps a reservation in this portcall' :
            top.Type() === IssueType.ReservationOverlapsVesselSchedule ?
                'overlaps same vessel at another location' :
            top.Type() === IssueType.ReservationOverlapsBerthClosure ?
                'overlaps a berth closure' :
            ''
        );
    },
    IssuesShortSummary = SuperCritical(({ issues, className, onClick } : { issues : () => Issue[], className? : string, onClick?: () => void }) => {
        const top = Issue.TopIssue(issues());
        return top === null ? null : <IssueSummary issue={top} className={className} onClick={onClick} />;
    }),
    IssueDescription = SuperCritical(({ issue } : { issue : Issue }) => {
        const res = issue.BerthReservation(),
            other = issue.OtherReservation(),
            otherpc = other && other.Itinerary() && other.Itinerary()!.PortCall(),
            berth = res ? (res.Itinerary() && res.Itinerary()!.Location() && res.Itinerary()!.Location()!.Berth()) : issue.Berth(),
            loc = berth && berth.Location(),
            brbcl = issue.BerthReservationBerthClosure();
        return (
            <div className={"issue-description alert alert-" + (issue.Severity() >= IssueSeverity.Warning ? "warning" : "info")}>
                {
                    issue.Type() === IssueType.ReservationOverlapsOther ?
                        <span>
                            Reservation{loc && (' at ' + loc.Name())} overlaps with an existing reservation{otherpc && ': '}{otherpc && <span className="issue-descr-vessel">{otherpc.DisplayVesselIdentifier()}</span>}.
                            {' '}
                            <span className="issue-description-follow-up">This reservation may be booked, changed or cancelled after a berth manager reviews it.</span>
                        </span> :
                    issue.Type() === IssueType.ReservationOverlapsShift ?
                        <span>
                            Times for reservation{loc && (' at ' + loc.Name())} overlap with another reservation for the same PortCall{otherpc && ': '}{otherpc && <span className="issue-descr-vessel">{otherpc.DisplayVesselIdentifier()}</span>}.
                            {' '}
                            <span className="issue-description-follow-up">This reservation may be booked, changed or cancelled after a berth manager reviews it.</span>
                        </span> :
                    issue.Type() === IssueType.ReservationOverlapsVesselSchedule ?
                        <span>
                            Times for reservation{loc && (' at ' + loc.Name())} overlap with an existing reservation for the same vessel{otherpc && ': '}{otherpc && <span><span className="issue-descr-vessel">{otherpc.DisplayVesselIdentifier()}</span> at {otherpc.Port()?.Name()}</span>}.
                            {' '}
                            <span className="issue-description-follow-up">This reservation may be booked, changed or cancelled after a berth manager reviews it.</span>
                        </span> :
                    issue.Type() === IssueType.ReservationOverlapsBerthClosure ?
                        <span>
                            Reservation{loc && (' at ' + loc.Name())} overlaps with an existing reservation{brbcl && ': '}{brbcl && <span className="issue-descr-closure">Closure &mdash; {brbcl.Title()}</span>}.
                            {' '}
                            <span className="issue-description-follow-up"> This reservation may be booked, changed or cancelled after a berth manager reviews it.</span>
                        </span> :
                    issue.Type() === IssueType.ReservationExceedsBerthDepth ?
                        <span>
                            This vessel exceeds the max. draft allowed{loc && (' at ' + loc.Name())}.
                            {' '}
                            <span className="issue-description-follow-up">This reservation may be booked, changed or cancelled after a berth manager reviews it.</span>
                        </span> :
                    issue.Type() === IssueType.ReservationExceedsBerthLength ?
                        <span>
                            This vessel exceeds the max. length allowed{loc && (' at ' + loc.Name())}.
                            {' '}
                            <span className="issue-description-follow-up">This reservation may be booked, changed or cancelled after a berth manager reviews it.</span>
                        </span> :
                    issue.Type() === IssueType.ReservationLacksKnownVessel ?
                        <span>
                            {loc ? loc.Name() : 'This berth'} requires reservations to have a vessel listed.
                            {' '}
                            <span className="issue-description-follow-up">This reservation will not be reviewed until a vessel is entered.</span>
                        </span> :
                    issue.Type() === IssueType.ScheduleDisagreesWithEnteredDate ?
                        <span>
                            {ScheduleDisagreesWithEnteredDateDescription(issue)}
                        </span> :
                    null
                }
            </div>
        );
    }),
    IssueDescriptionForTooltip = SuperCritical(({ issue } : { issue : Issue }) => {
        const res = issue.BerthReservation(),
            other = issue.OtherReservation(),
            otherpc = other && other.Itinerary() && other.Itinerary()!.PortCall(),
            berth = res ? (res.Itinerary() && res.Itinerary()!.Location() && res.Itinerary()!.Location()!.Berth()) : issue.Berth(),
            loc = berth && berth.Location(),
            brbcl = issue.BerthReservationBerthClosure();
        return (
            <div className="issue-description">
                {
                    issue.Type() === IssueType.ReservationOverlapsOther ?
                        <div className="tooltip-issues">
                            Reservation{loc && (' at ' + loc.Name())} overlaps with an existing reservation{otherpc && ': '}{otherpc && <span className="issue-descr-vessel">{otherpc.DisplayVesselIdentifier()}</span>}.
                        </div> :
                    issue.Type() === IssueType.ReservationOverlapsShift ?
                        <div className="tooltip-issues">
                            Times for reservation{loc && (' at ' + loc.Name())} overlap with another reservation for the same PortCall{otherpc && ': '}{otherpc && <span className="issue-descr-vessel">{otherpc.DisplayVesselIdentifier()}</span>}.
                        </div> :
                    issue.Type() === IssueType.ReservationOverlapsVesselSchedule ?
                        <div className="tooltip-issues">
                            Times for reservation{loc && (' at ' + loc.Name())} overlap with an existing reservation for the same vessel{otherpc && ': '}{otherpc && <span><span className="issue-descr-vessel">{otherpc.DisplayVesselIdentifier()}</span> at {otherpc.Port()?.Name()}</span>}.
                        </div> :
                    issue.Type() === IssueType.ReservationOverlapsBerthClosure ?
                        <div className="tooltip-issues">
                            Reservation{loc && (' at ' + loc.Name())} overlaps with an existing reservation{brbcl && ': '}{brbcl && <span className="issue-descr-closure">Closure &mdash; {brbcl.Title()}</span>}.
                        </div> :
                    issue.Type() === IssueType.ReservationExceedsBerthDepth ?
                        <div className="tooltip-issues">
                            This vessel exceeds the max. draft allowed{loc && (' at ' + loc.Name())}.
                        </div> :
                    issue.Type() === IssueType.ReservationExceedsBerthLength ?
                        <div className="tooltip-issues">
                            This vessel exceeds the max. length allowed{loc && (' at ' + loc.Name())}.
                        </div> :
                    issue.Type() === IssueType.ReservationLacksKnownVessel ?
                        <div className="tooltip-issues">
                            {loc ? loc.Name() : 'This berth'} requires reservations to have a vessel listed.
                            Enter a vessel for this PortCall or select a different location.
                        </div> :
                    issue.Type() === IssueType.PilotJobOrderExceedsBerthDepth ?
                        <div className="tooltip-issues">
                            This vessel exceeds the max. draft allowed for this movement.
                        </div> :
                    issue.Type() === IssueType.ScheduleDisagreesWithEnteredDate ?
                        <div className="tooltip-issues">
                            {ScheduleDisagreesWithEnteredDateDescription(issue)}
                        </div> :
                    null
                }
            </div>
        );
    }),
    /**
     * Get a medium-length string description of a ScheduleDisagreesWithEnteredDate Issue.
     */
    ScheduleDisagreesWithEnteredDateDescription = (issue : Issue) => {
        const
            ed = issue.EnteredDate(),
            edsd = ed && ed.ScheduleDate(),
            sd = issue.ScheduleDate(),
            evt = ed && ed.ScheduleChange() && ed.ScheduleChange()!.Event(),
            enteredName = ed && edsd ?
                ed.Type() === ScheduleDateEventType.EstimatedDateEntered &&
                edsd.Type() === ScheduleDateType.IPArrival ?
                    // special case -- 'ETA' instead of 'estimated arrival'
                    'ETA' :
                // Otherwise -- '{estimated/actual} {arrival/departure/start/end}'
                (ed.Type() === ScheduleDateEventType.EstimatedDateEntered ? 'estimated' : 'actual')
                    + ' '
                    + ScheduleDateName(edsd) :
                '',
            scheduledName = sd ?
                // '{estimated/actual} {arrival/departure/start/end}'
                (issue.DiscrepantDateField() === DiscrepantScheduleDateField.Estimated ? 'estimated' : 'actual')
                    + ' '
                    + ScheduleDateName(sd) :
                '',
            sourceName = evt && (
                evt.User() && evt.User()!.FullName() ||
                evt.Automation() && evt.Automation()!.Name()
            ) || '',
            entryAction = evt && ed ?
                evt.User() ? 'entered' :
                ed.Type() === ScheduleDateEventType.EstimatedDateEntered ? 'forecasted' :
                'provided' :
                '',
            sVal = sd && (
                issue.DiscrepantDateField() === DiscrepantScheduleDateField.Estimated ?
                    sd.Estimated() :
                    sd.Actual()
            ),
            eVal = ed && ed.Date(),
            deltaHours = sVal && eVal && tzmoment(eVal).diff(tzmoment(sVal), 'hours'),
            deltaText = deltaHours !== null ?
                `${Math.abs(deltaHours)} hour${deltaHours === 1 || deltaHours === -1 ? '' : 's'} ${deltaHours >= 0 ? 'after' : 'before'}` :
                '';
        return `The ${enteredName} ${entryAction} by ${sourceName} is more than ${deltaText} the current ${scheduledName}.`;
    };

function ScheduleDateName(sd : ScheduleDateEntity) {
    return (
        sd.Type() === ScheduleDateType.IPArrival ?
            'arrival' :
        sd.Type() === ScheduleDateType.IPDeparture ?
            'departure' :
        sd.Type() === ScheduleDateType.PJOStart ?
            'start' :
        sd.Type() === ScheduleDateType.PJOEnd ?
            'end' :
        ''
    );
}



// WEBPACK FOOTER //
// ./apps/portcall/issues/view.tsx